const MAX_AGE_MS = 30 * 60 * 1000;

export type BulkProcessQueuePersist = {
  stoppingIds: string[];
  processingIds: string[];
  /** Bulk run scope — drives global banner "processed N of K". */
  targetIds?: string[];
  /** applicationId → positionId for targets missing from the list cache on poll/resume. */
  targetRefs?: Record<string, string>;
  /** Last known banner completed count — survives F5 while batch is in-flight. */
  completed?: number;
  updatedAt: number;
};

/** Merge position ids for bulk targets so detail/status hydrate works off-list. */
export function mergeBulkTargetRefs(
  existing: Record<string, string> | undefined,
  apps: readonly { id: string; positionId?: string }[],
  targetIds: readonly string[],
): Record<string, string> {
  const refs = { ...(existing ?? {}) };
  for (const id of targetIds) {
    const app = apps.find((a) => a.id === id);
    if (app?.positionId) refs[id] = app.positionId;
  }
  return refs;
}

export function bulkQueueStorageKey(companyId: string): string {
  return `hiromatch:bulk-process-queue:${companyId}`;
}

/** Union of batch targets — preserves order (existing first, then new). */
export function mergeBulkTargetIds(
  existing: readonly string[],
  incoming: readonly string[],
): string[] {
  const seen = new Set(existing);
  const merged = [...existing];
  for (const id of incoming) {
    if (seen.has(id)) continue;
    seen.add(id);
    merged.push(id);
  }
  return merged;
}

function readPersisted(companyId: string): BulkProcessQueuePersist | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(bulkQueueStorageKey(companyId));
    if (!raw) return null;
    return JSON.parse(raw) as BulkProcessQueuePersist;
  } catch {
    return null;
  }
}

function writePersisted(companyId: string, value: BulkProcessQueuePersist): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(bulkQueueStorageKey(companyId), JSON.stringify(value));
  } catch {
    // private mode / quota
  }
}

export function loadBulkProcessQueue(companyId: string): {
  stoppingIds: Set<string>;
  processingIds: Set<string>;
  targetIds: string[];
  targetRefs: Record<string, string>;
  completed: number;
} {
  const raw = readPersisted(companyId);
  if (!raw || Date.now() - raw.updatedAt > MAX_AGE_MS) {
    return {
      stoppingIds: new Set(),
      processingIds: new Set(),
      targetIds: [],
      targetRefs: {},
      completed: 0,
    };
  }
  return {
    stoppingIds: new Set(raw.stoppingIds),
    processingIds: new Set(raw.processingIds),
    targetIds: Array.isArray(raw.targetIds) ? raw.targetIds : [],
    targetRefs: raw.targetRefs && typeof raw.targetRefs === 'object' ? raw.targetRefs : {},
    completed: typeof raw.completed === 'number' ? Math.max(0, raw.completed) : 0,
  };
}

export function clearBulkProcessQueue(companyId: string): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(bulkQueueStorageKey(companyId));
  } catch {
    // ignore
  }
}

export function saveBulkProcessQueue(
  companyId: string,
  stoppingIds: Set<string>,
  processingIds: Set<string>,
  targetIds: string[] = [],
  completed?: number,
  targetRefs?: Record<string, string>,
): void {
  if (stoppingIds.size === 0 && processingIds.size === 0 && targetIds.length === 0) {
    clearBulkProcessQueue(companyId);
    return;
  }
  const existing = readPersisted(companyId);
  writePersisted(companyId, {
    stoppingIds: [...stoppingIds],
    processingIds: [...processingIds],
    targetIds: targetIds.length > 0 ? targetIds : undefined,
    targetRefs: targetRefs && Object.keys(targetRefs).length > 0
      ? targetRefs
      : existing?.targetRefs,
    completed: completed ?? existing?.completed,
    updatedAt: Date.now(),
  });
}
